import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useResend2FACode } from '@/hooks/queries/useAuthQueries';
import { useTwoFactorForm, useTwoFactorActions, useTwoFactorModal } from '@/stores/twoFactorStore';
import { TwoFactorMethod } from '@/types/misc';
import { ErrorDisplay } from '../ErrorDisplay';
import { Button } from '../ui/Button';
import { MaskedTextField } from '../ui/MaskedTextField';
import { Modal } from '../ui/Modal';

type TwoFactorModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const RESEND_COOLDOWN_SECONDS = 6; // 1 minute

export const TwoFactorModal: React.FC<TwoFactorModalProps> = ({ isOpen, onClose }) => {
  const { method, error, pendingRequest } = useTwoFactorForm();
  const { sessionId } = useTwoFactorModal();
  const { clear, setError } = useTwoFactorActions();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resendMutation = useResend2FACode();

  // Timer countdown effect
  useEffect(() => {
    if (!isOpen || resendTimer <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, resendTimer]);

  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setError(undefined);
      setIsSubmitting(false);
      setResendTimer(RESEND_COOLDOWN_SECONDS);
      setIsResending(false);
    }
  }, [isOpen, setError]);

  const handleSubmit = useCallback(async () => {
    if (code.trim().length === 0 || !pendingRequest) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Execute the pending request with the code
      // The middleware will handle storing the result
      const result = await pendingRequest.execute(code.trim());
      const error = result.error;

      // If we get here, the request completed
      if (error) {
        // Error is already set in store by middleware, just clear code for retry
        setCode('');
        setIsSubmitting(false);
      }
      // If no error, the middleware stored the result and waitForTwoFAResult() will pick it up
      // The modal will close automatically when isRequired becomes false
    } catch (error) {
      // This shouldn't happen if apiClient handles it properly, but just in case
      setError(error instanceof Error ? error.message : 'Verification failed');
      setIsSubmitting(false);
    }
  }, [code, pendingRequest, setError]);

  const handleResend = useCallback(async () => {
    if (!sessionId || resendTimer > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setError(undefined);

    try {
      await resendMutation.mutateAsync(sessionId);
      setResendTimer(RESEND_COOLDOWN_SECONDS);
      setCode('');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }, [sessionId, resendTimer, isResending, resendMutation, setError]);

  const handleClose = useCallback(() => {
    clear();
    onClose();
  }, [onClose, clear]);

  const methodLabel = method === TwoFactorMethod.PHONE ? 'phone' : 'email';
  const title = 'Two-Factor Authentication';
  const subtitle = `We've sent a verification code to your ${methodLabel}. Please enter the code below to continue.`;
  const maxLength = 6;
  const mask = '_'.repeat(maxLength);
  const errors = error ? [error] : undefined;
  const showErrors = !!error;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={500}
      header={
        <div
          className="text-center text-black text-2xl font-medium break-words"
          style={{ fontFamily: 'var(--ep-font-avenir)' }}
        >
          {title}
        </div>
      }
    >
      <div className="p-8 flex flex-col gap-6">
        <div className="w-full flex flex-col items-center gap-3">
          <div className="self-stretch flex flex-col items-center gap-1 px-[70px]">
            <div className="self-stretch text-center text-black text-[14px] font-avenir font-medium break-words">
              Verify your {methodLabel}
            </div>
            <div className="text-center text-[#4A5565] text-[12px] font-avenir font-normal leading-5 break-words">
              {subtitle}
            </div>
          </div>

          <div className="flex flex-col items-start gap-2">
            <MaskedTextField
              ref={inputRef}
              value={code}
              onChange={setCode}
              mask={mask}
              disabled={isSubmitting}
              autoFocus
            />
            {showErrors && errors && <ErrorDisplay errors={errors} />}
          </div>

          {/* Resend Code Section */}
          <div className="flex flex-col items-center gap-2">
            <div className="text-center text-[#4A5565] text-[12px] font-avenir font-normal leading-5 break-words">
              Didn't receive the code?
            </div>
            {resendTimer > 0 ? (
              <div className="text-center text-[#4A5565] text-[12px] font-avenir font-normal break-words">
                Resend code in {resendTimer}s
              </div>
            ) : (
              <Button onClick={handleResend} variant="ghost" disabled={isResending || !sessionId} loading={isResending}>
                Resend code
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-4 justify-end mt-4">
          <Button onClick={handleClose} variant="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            disabled={isSubmitting || code.trim().length === 0}
            loading={isSubmitting}
          >
            Verify
          </Button>
        </div>
      </div>
    </Modal>
  );
};
